import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatCardModule } from '@angular/material/card';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatMenuModule } from '@angular/material/menu';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { concatMap, delay, of, Subscription } from 'rxjs';
import { MessageResponseComponent } from './components/message-response/message-response.component';
import { ChatService } from './services/chat.service';
import { FontService } from './services/font.service';
import {
  TYPING_INDICATOR_INTERVAL,
  BotMessageType,
  BOT_TYPING_INTERVAL,
  MESSAGE_READ_INTERVAL,
  RESTART_POPUP_MESSAGE,
  IframeMessageType,
} from './utils/constants';
import { Message } from './models/chat-message';
import { FileUploadComponent } from './components/file-upload/file-upload.component';
import { RestartPopupComponent } from './components/common/restart-popup/restart-popup.component';
import { ChatlinkPopupComponent } from './components/common/chatlink-popup/chatlink-popup.component';
import { IConfig } from '../chat/models/agency-config';

@Component({
  selector: 'app-chat',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatButtonModule,
    MatCardModule,
    MatInputModule,
    MatIconModule,
    MatButtonToggleModule,
    MatFormFieldModule,
    MessageResponseComponent,
    FileUploadComponent,
    MatMenuModule,
    ClipboardModule,
    RestartPopupComponent,
    ChatlinkPopupComponent,
    MatProgressSpinnerModule,
  ],
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss'],
})
export class ChatComponent implements OnInit {
  @ViewChild('chatContent') private chatContent!: ElementRef;
  @ViewChild('fileInput') fileInput!: ElementRef;
  @ViewChild('chatInput') chatInput!: ElementRef<HTMLInputElement>;

  message: string = '';
  canSendMessage: boolean = true;
  chatWindowOpened: boolean = false;
  botIsTyping: boolean = false;
  selectedFiles: File[] = [];
  chatInputVisible: boolean = false;
  subscription: Subscription | null = null;
  showRestartDialog: boolean = false;
  restartInfo: any = {
    message: '',
    showCancel: false,
  };
  showChatLinkDialog: boolean = false;
  previousConversationId: number = 0;

  chatLink: string = '';
  constructor(
    private chatService: ChatService,
    private fontService: FontService,
  ) {}

  ngOnInit() {
    this.chatService.newMessage.subscribe(() => {
      this.botIsTyping = false;
      this.scrollToBottom();
    });
    this.chatService.init().subscribe((d) => {
      const fonts = this.configs?.fonts;
      if (fonts) {
        this.fontService.applyFonts(fonts);
      }

      window.parent.postMessage(
        {
          session: d.userId,
          botEnabled: d.botEnabled,
          agencyConfig: this.configs,
        },
        '*',
      );
      if (this.configs?.chat_window_start_state === 1) this.toggleChat();

      this.chatInputVisible =
        this.configs && this.configs.script.flavour > 0 ? true : false;
    });

    window.addEventListener('focus', () => {
      this.readMessages();
    });

    window.addEventListener('click', () => {
      this.readMessages();
    });
  }

  ngAfterViewInit() {
    if (this.chatWindowOpened) {
      setTimeout(() => this.chatInput?.nativeElement?.focus(), 0);
    }
  }

  get botEnabled() {
    return this.chatService.botEnabled;
  }

  get configs() {
    return this.chatService.configs;
  }

  get fileUploadRequired() {
    return this.chatService.fileUploadRequired;
  }

  get uploadedFiles() {
    return this.chatService.uploadedFiles;
  }

  conversation: Message[] = [];

  sendMessage(event: any) {
    event.preventDefault();
    if (
      (!this.message && this.selectedFiles.length === 0) ||
      !this.canSendMessage
    )
      return;

    this.canSendMessage = false;
    this.botIsTyping = true;
    setTimeout(() => {
      this.botIsTyping = false;
    }, TYPING_INDICATOR_INTERVAL);

    this.chatService.send(this.message, '').subscribe({
      next: (d) => {
        this.message = '';
        this.resetChatInput();
        setTimeout(() => this.chatInput?.nativeElement?.focus(), 0);
      },
      error: (err) => {
        this.canSendMessage = true;
      }
    });

    this.message = '';
  }

  sendResponse($event: any) {
    if (!this.canSendMessage) return;

    const messageText = $event.message_text;
    const buttonId = $event.button_id;
    const galleryButtonId = $event.galleryButtonId;
    const type = $event.type;

    this.canSendMessage = false;

    if (type && type === 'load_previous_conversation') {
      const previousConversationId = $event?.previousConversationId || 0;
      if (previousConversationId > 0) {
        this.chatService.send(messageText, buttonId).subscribe({
          next: (_) => { this.canSendMessage = true; },
          error: (err) => { this.canSendMessage = true; }
        });
        this.loadPreviousConversation(previousConversationId);
      } else {
        this.canSendMessage = true;
      }
      return;
    } else {
      this.chatService
        .send(messageText, buttonId, galleryButtonId)
        .subscribe({
          next: (_) => { this.canSendMessage = true; },
          error: (err) => { this.canSendMessage = true; }
        });
    }
  }

  toggleChat() {
    this.chatWindowOpened = !this.chatWindowOpened;
    if (this.chatWindowOpened) {
      if (!this.subscription || this.previousConversationId > 0) {
        this.chatService.getAgencyConfig().subscribe((response) => {
          if (response.userId === 0) {
            this.showRestartDialog = true;
            this.restartInfo = {
              message: RESTART_POPUP_MESSAGE,
              showCancel: false,
            };
          }

          window.parent.postMessage(
            {
              session: response.userId,
              botEnabled: response.botEnabled,
              agencyConfig: this.configs,
              sessionId: response.sessionId,
            },
            '*',
          );
          this.chatService.getHistory();
          this.previousConversationId = 0;
        });
      }

      this.scrollToBottom();
      setTimeout(() => this.chatInput?.nativeElement?.focus(), 0);
    }
    this.notifyChatWindowSizeChanges(!this.chatWindowOpened);

    this.subscribeToConversation();
  }

  private notifyChatWindowSizeChanges(minimized: boolean = false) {
    if (!minimized) {
      window.parent.postMessage({ config: { minimized: false } }, '*');
    } else {
      window.parent.postMessage({ config: { minimized: true } }, '*');
    }
  }

  private scrollToBottom(): void {
    if (!this.chatWindowOpened) return;
    window.setTimeout(() => {
      try {
        this.chatContent.nativeElement.scrollTop =
          this.chatContent.nativeElement.scrollHeight;
      } catch (err) {
        console.error('Scroll to bottom failed!', err);
      }
    }, 300);
  }

  onFilesSelected(files: File[]): void {
    this.selectedFiles = files;
  }

  onCancelUpload(): void {
    this.selectedFiles = [];
  }

  private subscribeToConversation() {
    if (!this.chatWindowOpened || this.subscription) return;

    this.subscription = this.chatService.conversation.subscribe((messages) => {
      of(...messages)
        .pipe(
          concatMap((message, index) => {
            if (
              index > 0 &&
              messages[index - 1].content.type == BotMessageType.TYPING
            ) {
              return of(message).pipe(delay(BOT_TYPING_INTERVAL));
            } else {
              return of(message);
            }
          }),
        )
        .subscribe((message) => {
          if (message.content.type === BotMessageType.REMOVE_TYPING) {
            this.conversation.pop();
            this.resetChatInput();
          } else {
            this.conversation.push(message);
            this.scrollToBottom();
          }
        });

      this.readMessages();
    });
  }

  restartChat() {
    this.chatService.clearCache();
    window.parent.postMessage(
      {
        type: IframeMessageType.RESTART_CHAT,
      },
      '*',
    );
    this.showRestartDialog = false;
  }
  cancelRestartChat() {
    this.showRestartDialog = false;
  }

  openRestartDialog() {
    this.showRestartDialog = true;
    this.restartInfo = {
      message: '',
      showCancel: true,
    };
  }

  private resetChatInput() {
    this.message = '';
    this.selectedFiles = [];
    this.canSendMessage = true;
    setTimeout(() => this.chatInput?.nativeElement?.focus(), 0);
  }

  readMessages() {
    if (!this.chatWindowOpened) return;

    setTimeout(() => {
      if (!document.hasFocus()) return;
      this.chatService.readMessages().subscribe((_) => {});
    }, MESSAGE_READ_INTERVAL);
  }

  closeChatLinkDialog() {
    this.showChatLinkDialog = false;
  }

  openChatLinkDialog() {
    const chatLink = this.chatService.getChatLink();

    if (chatLink) {
      this.chatLink = chatLink;
      this.showChatLinkDialog = true;
    }
  }

  loadPreviousConversation(prevConversationId: number) {
    this.previousConversationId = prevConversationId;
    this.conversation = [];
    this.chatService.resetConversation(prevConversationId, false);
    this.chatWindowOpened = false;

    window.parent.postMessage(
      {
        type: IframeMessageType.LOAD_PREVIOUS_CONVERSATION,
      },
      '*',
    );

    this.toggleChat();
  }
}
